import { z } from "zod";
import { PlatformTypeZ } from "./platform";

/**
 * Structured rules extracted from a firm's rulebook (either propfirmmatch.com
 * or the firm's own site). Shape is intentionally a superset — fields are
 * optional/nullable because different firms publish different data.
 *
 * This schema is passed to Cloudflare Browser Rendering's `/json` endpoint
 * as `response_format.json_schema`, so the shape must be a flat JSON-Schema-
 * compatible zod tree (no refinements, no unions of objects).
 */
/**
 * One challenge program / account type. Every field is `.nullable().optional()`
 * because zod-to-json-schema translates `.nullable()` alone to a required
 * field with null allowed, which causes CF Browser Rendering's JSON Schema
 * validator to reject AI output the moment ANY single field is omitted.
 *
 * Only `name` is effectively required (we need something to identify the
 * account type in downstream code), but even that stays optional so
 * partial AI outputs don't blow up.
 */
export const FirmAccountTypeZ = z.object({
  name: z.string().optional(),
  // 0 = instant funding (no evaluation phase). 1/2/3 = eval phases.
  // No min/max bounds — zod-to-json-schema emits them as `minimum`/
  // `maximum` on an `"type": "integer"` and Anthropic's structured-output
  // API rejects that combo with "For 'integer' type, properties maximum,
  // minimum are not supported". Validate the range downstream instead.
  phases: z.number().int().nullable().optional(),
  // Challenge family slug — "instant" / "1-step" / "2-step" / "3-step".
  // Redundant with `phases` but kept as a sibling because dashboard
  // filters + RAG queries key off challenge_type strings everywhere,
  // and zero-phase + "instant" is clearer than forcing consumers to
  // translate 0 → "instant".
  challengeType: z.string().nullable().optional(),
  initialBalances: z.array(z.number()).nullable().optional(),
  // Array of per-phase numbers OR nulls (Instant programs may report
  // [null] or null for no target). Nullable at the element level so
  // "10% 5%" parses to [10, 5] and "None 5%" parses to [null, 5].
  profitTargetPct: z.array(z.number().nullable()).nullable().optional(),
  dailyDrawdownPct: z.number().nullable().optional(),
  maxDrawdownPct: z.number().nullable().optional(),
  drawdownType: z.string().nullable().optional(), // was enum — AI variance too high
  drawdownBasis: z.string().nullable().optional(),
  minTradingDays: z.number().int().nullable().optional(),
  consistencyRulePct: z.number().nullable().optional(),
  newsTradingAllowed: z.boolean().nullable().optional(),
  newsBlackoutMinutesBefore: z.number().int().nullable().optional(),
  newsBlackoutMinutesAfter: z.number().int().nullable().optional(),
  weekendHoldingAllowed: z.boolean().nullable().optional(),
  fridayCutoffUtc: z.string().nullable().optional(),
  allowedInstruments: z.array(z.string()).nullable().optional(),
  platforms: z.array(z.string()).nullable().optional(), // was PlatformTypeZ enum
  payoutSplitPct: z.number().nullable().optional(),
  // Display string as shown on the page — "14 days", "Bi-weekly",
  // "On-demand", "Bi-weekly | Weekly with add-on", etc. Storing the raw
  // text keeps the RAG chunk human-readable for Haiku; structured
  // parsing is downstream (firm_challenges.payout_frequency + per-firm
  // payout_policy_json on firm_profiles).
  payoutFrequency: z.string().nullable().optional(),
  scalingPlan: z.string().nullable().optional(),
  // Per-asset-class leverage for THIS account type. Keys are asset names
  // (FX/Crypto/Metals/Indices/Energy/Stocks/Other Commodities), values
  // are display strings like "1:100", "1:30", "n/a". Firms bucket
  // leverage by (asset × program) — the firm-level `FirmRulesZ.leverage`
  // matrix is per-phase-count, but Moneta/FundingPips/others diverge per
  // named program even within the same phase count.
  leveragePerAsset: z.record(z.string(), z.string()).nullable().optional(),
});
export type FirmAccountType = z.infer<typeof FirmAccountTypeZ>;

/**
 * Rules extracted from a PFM or official firm page.
 *
 * Shape note: all firm-level fields are optional/nullable because the
 * same schema is reused across very different page layouts. The AI
 * fills what it can find; unset fields land as null so RAG + downstream
 * logic can reason about what data is actually known vs. unknown.
 *
 * The `accountTypes` array holds the structured per-challenge rules
 * that the old schema only tracked. Everything else (firm metadata,
 * per-asset leverage / commissions, policy blurbs) is additive — older
 * rows keep working since new fields are nullable.
 */
export const FirmRulesZ = z.object({
  // Per-account-type structured rules. Optional so AI can omit the
  // array entirely on pages that only carry firm-level metadata.
  accountTypes: z.array(FirmAccountTypeZ).nullable().optional(),

  // ─── Firm-level metadata (v2 additive) ────────────────────────
  ceo: z.string().nullable().optional(),
  country: z.string().nullable().optional(), // ISO 3166-1 alpha-2
  yearFounded: z.number().int().nullable().optional(),
  trustpilotRating: z.number().nullable().optional(),
  trustpilotReviewCount: z.number().int().nullable().optional(),
  broker: z.string().nullable().optional(),

  // Lists of things the firm supports (page-level, not per account type)
  platforms: z.array(PlatformTypeZ).nullable().optional(),
  assets: z.array(z.string()).nullable().optional(),
  paymentMethods: z.array(z.string()).nullable().optional(),
  payoutMethods: z.array(z.string()).nullable().optional(),

  // Per-asset-class tables. Keys are asset categories (FX, Crypto,
  // Indices, Metals, ...). Values are dict-of-challenge-phase →
  // display string. AI fills as visible; don't type-check contents.
  leverage: z.record(z.string(), z.record(z.string(), z.string())).nullable().optional(),
  commissions: z.record(z.string(), z.string()).nullable().optional(),

  // Free-form policy prose. Keys are section headings as shown on
  // the page ("News Trading", "Copy Trading", "Expert Advisors",
  // "Consistency", "Payout Policy", ...). Values are verbatim blurbs.
  // Critical for RAG — the structured booleans can't capture nuance
  // like "news trading allowed with 5 min blackout on Instant only".
  policyBlurbs: z.record(z.string(), z.string()).nullable().optional(),

  // Per-account-type detail blurbs (same idea, keyed by account name)
  accountTypeBlurbs: z.record(z.string(), z.string()).nullable().optional(),
});
export type FirmRules = z.infer<typeof FirmRulesZ>;

// Same rationale as FirmAccountTypeZ — every field optional so partial
// AI extraction passes validation.
export const FirmPricingSampleZ = z.object({
  accountSize: z.number().nullable().optional(),
  accountType: z.string().nullable().optional(),
  basePrice: z.number().nullable().optional(),
  effectivePrice: z.number().nullable().optional(),
  discountCode: z.string().nullable().optional(),
  discountPct: z.number().nullable().optional(),
  promoName: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(), // ISO-8601 if present
  currency: z.string().nullable().optional(),
});
export type FirmPricingSample = z.infer<typeof FirmPricingSampleZ>;

// Hero-banner / sticky-bar / checkout-widget style discount that
// applies to the whole site (e.g. "10% OFF everything with BF10",
// "40% OFF — code AQUA"). These don't fit the per-account-size
// `samples[]` shape because they're site-wide, and AI was dropping
// them entirely when it couldn't attach a code to a specific size.
export const FirmSiteWideDiscountZ = z.object({
  code: z.string().nullable().optional(),
  pct: z.number().nullable().optional(),
  promoName: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(),
});
export type FirmSiteWideDiscount = z.infer<typeof FirmSiteWideDiscountZ>;

export const FirmPricingZ = z.object({
  samples: z.array(FirmPricingSampleZ).nullable().optional(),
  siteWideDiscount: FirmSiteWideDiscountZ.nullable().optional(),
  scrapedAt: z.number().int().nullable().optional(),
});
export type FirmPricing = z.infer<typeof FirmPricingZ>;

export const FirmAvailabilityZ = z.object({
  countries: z.array(z.string()).nullable().optional(), // ISO 3166-1 alpha-2
  restrictedCountries: z.array(z.string()).nullable().optional(),
  turkeyAllowed: z.boolean().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type FirmAvailability = z.infer<typeof FirmAvailabilityZ>;

/**
 * Top-level payload for /admin/firms/:slug/intel and for the FirmIntelCrawl
 * workflow's `step.do()` results.
 */
export const FirmIntelZ = z.object({
  slug: z.string().min(1).max(32),
  source: z.enum(["propfirmmatch", "official"]),
  sourceUrl: z.string().url(),
  scrapedAt: z.number().int(),
  rules: FirmRulesZ.nullable(),
  pricing: FirmPricingZ.nullable(),
  availability: FirmAvailabilityZ.nullable(),
  rawMarkdown: z.string().optional(), // set when we also fetch /markdown
});
export type FirmIntel = z.infer<typeof FirmIntelZ>;
